/**
 * La portée de la liste des concepts — une seule, partagée avec « Rapprocher » (C31 bis SP1,
 * spec § 1.4 ; née en C31 SP1 pour la seule fenêtre « Concepts à rapprocher ») — gardée par
 * navigateur comme `filtresMemorises` du graphe : `localStorage`, jamais au serveur. Une valeur
 * illisible retombe sur « Tous mes concepts » ; aucune lecture ne casse l'écran. L'ancienne clé
 * de C31 est reprise une fois, puis retirée.
 */
import type { PorteeRapprochement } from '../../types/api';

export const PORTEE_CONCEPTS_KEY = 'anagogia.concepts.portee';
/** Clé de C31 SP1 (« Rapprocher » seul) : lue une fois en repli, puis retirée. */
export const ANCIENNE_PORTEE_RAPPROCHER_KEY = 'anagogia.rapprocher.portee';

const TOUS: PorteeRapprochement = { sorte: 'tous' };

/** La portée d'une valeur lue (pure, testable sans stockage). */
export function porteeDepuis(lu: unknown): PorteeRapprochement {
  if (!lu || typeof lu !== 'object') return TOUS;
  const v = lu as Record<string, unknown>;
  if ((v.sorte === 'vocabulaire' || v.sorte === 'dossier') && typeof v.id === 'string' && v.id) return { sorte: v.sorte, id: v.id };
  return TOUS;
}

/** « Tous » quand le vocabulaire ou le dossier de la portée n'existe plus (pure). */
export function porteeValide(portee: PorteeRapprochement, vocabulaireIds: ReadonlySet<string>, dossierIds: ReadonlySet<string>): PorteeRapprochement {
  if (portee.sorte === 'vocabulaire') return vocabulaireIds.has(portee.id) ? portee : TOUS;
  if (portee.sorte === 'dossier') return dossierIds.has(portee.id) ? portee : TOUS;
  return portee;
}

const lire = (cle: string): unknown => {
  const brut = localStorage.getItem(cle);
  return brut ? JSON.parse(brut) : null;
};

export function lirePorteeMemorisee(): PorteeRapprochement {
  try {
    const courante = lire(PORTEE_CONCEPTS_KEY);
    if (courante) return porteeDepuis(courante);
    const ancienne = lire(ANCIENNE_PORTEE_RAPPROCHER_KEY);
    if (!ancienne) return TOUS;
    // Repli de C31 : la valeur passe sous la nouvelle clé, l'ancienne s'efface (une fois).
    const portee = porteeDepuis(ancienne);
    localStorage.setItem(PORTEE_CONCEPTS_KEY, JSON.stringify(portee));
    localStorage.removeItem(ANCIENNE_PORTEE_RAPPROCHER_KEY);
    return portee;
  } catch {
    return TOUS;
  }
}

export function ecrirePorteeMemorisee(portee: PorteeRapprochement): void {
  try {
    localStorage.setItem(PORTEE_CONCEPTS_KEY, JSON.stringify(portee));
  } catch {
    /* stockage indisponible (navigation privée) : le réglage vaut pour la séance */
  }
}
